/**
 * Infrastructure screen — real-time platform health for staff. Three
 * tabs (Health / Components / Sessions) with tab-specific polling
 * cadences (10s for Health, 60s for the other two).
 *
 * Two improvements over the legacy view on app.nurev.io:
 *   - `inference` component type badge added (the legacy `typeColors`
 *     map predated Inference Phase 2, so InferenceWorker rows fell
 *     back to the default neutral badge).
 *   - Session timeline bars now have a stable max-value memoization
 *     pass instead of recomputing inside the render loop (cheap, but
 *     the legacy hot-path recomputed N×N on every paint).
 */
import { useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import {
  ComponentRow,
  ComponentType,
  TimelineRow
} from '@/api/adminInfra';
import {
  useInfraComponents,
  useInfraHealth,
  useSessionsTimeline
} from '@/hooks/useAdminInfra';
import {
  Card,
  SectionLabel,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import { fmtNum } from '@/lib/format';

const formatUptime = (secs?: number): string => {
  if (!secs) return '—';
  const d = Math.floor(secs / 86_400);
  const h = Math.floor((secs % 86_400) / 3_600);
  const m = Math.floor((secs % 3_600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const TYPE_BADGE: Record<string, string> = {
  'cpu-miner': 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30',
  'gpu-miner': 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  'bandwidth-relay': 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  'storage-node': 'bg-orange-500/20 text-orange-300 ring-1 ring-orange-500/30',
  inference: 'bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/30',
  shell: 'bg-pink-500/20 text-pink-300 ring-1 ring-pink-500/30'
};

function TypeBadge({ type }: { type: ComponentType | string }) {
  const cls =
    TYPE_BADGE[type] || 'bg-white/10 text-nurev-muted ring-1 ring-white/10';
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${cls}`}
    >
      {type}
    </span>
  );
}

// ─── Health tab ───────────────────────────────────────────────────────────

function HealthTab() {
  const { data, isLoading } = useInfraHealth();

  const sessions = data?.sessions || { active: 0, healthy: 0, stale: 0 };
  const compute = data?.compute || {
    totalSpeed: 0,
    totalThreads: 0,
    sharesAccepted: 0,
    sharesRejected: 0,
    gpuMiners: 0
  };
  const platforms = data?.platforms || {};
  const sources = data?.sources || {};

  const healthPct =
    sessions.active > 0 ? Math.round((sessions.healthy / sessions.active) * 100) : 100;
  const shareTotal = compute.sharesAccepted + compute.sharesRejected;
  const shareAcceptPct =
    shareTotal > 0 ? Math.round((compute.sharesAccepted / shareTotal) * 100) : null;

  const platformTotal = useMemo(
    () => Object.values(platforms).reduce((a, b) => a + b, 0),
    [platforms]
  );
  const sourceTotal = useMemo(
    () => Object.values(sources).reduce((a, b) => a + b, 0),
    [sources]
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <StatCard
          label="Active Sessions"
          value={fmtNum(sessions.active)}
          sub={`${sessions.healthy} healthy · ${sessions.stale} stale`}
          color={sessions.stale > 0 ? 'text-amber-300' : 'text-emerald-300'}
          loading={isLoading}
        />
        <StatCard
          label="Session Health"
          value={`${healthPct}%`}
          sub={`${sessions.healthy} / ${sessions.active} recent heartbeat`}
          color={
            healthPct >= 90
              ? 'text-emerald-300'
              : healthPct >= 70
                ? 'text-amber-300'
                : 'text-red-300'
          }
          loading={isLoading}
        />
        <StatCard
          label="Total Speed"
          value={fmtNum(compute.totalSpeed)}
          sub={`${fmtNum(compute.totalThreads)} threads · ${compute.gpuMiners} GPU`}
          loading={isLoading}
        />
        <StatCard
          label="Share Accept Rate"
          value={shareAcceptPct !== null ? `${shareAcceptPct}%` : '—'}
          sub={`${fmtNum(compute.sharesAccepted)} accepted · ${fmtNum(
            compute.sharesRejected
          )} rejected`}
          color={
            shareAcceptPct === null
              ? undefined
              : shareAcceptPct >= 95
                ? 'text-emerald-300'
                : shareAcceptPct >= 85
                  ? 'text-amber-300'
                  : 'text-red-300'
          }
          loading={isLoading}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <StatCard
          label="Active Components"
          value={fmtNum(data?.components?.enabled)}
          loading={isLoading}
        />
        <StatCard
          label="Pending Withdrawals"
          value={fmtNum(data?.withdrawals?.pending)}
          color={
            (data?.withdrawals?.pending || 0) > 0
              ? 'text-amber-300'
              : undefined
          }
          loading={isLoading}
        />
        <StatCard
          label="New Installs (1h)"
          value={fmtNum(data?.newInstalls1h)}
          color="text-emerald-300"
          loading={isLoading}
        />
        <StatCard
          label="API Uptime"
          value={formatUptime(data?.uptimeSeconds)}
          loading={isLoading}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <BreakdownCard
          title="Active Sessions by Platform"
          loading={isLoading}
          rows={platforms}
          total={platformTotal}
          accent="bg-amber-500"
        />
        <BreakdownCard
          title="Active Sessions by Source"
          loading={isLoading}
          rows={sources}
          total={sourceTotal}
          accent="bg-emerald-500"
        />
      </div>
    </div>
  );
}

function BreakdownCard({
  title,
  loading,
  rows,
  total,
  accent
}: {
  title: string;
  loading: boolean;
  rows: Record<string, number>;
  total: number;
  accent: string;
}) {
  const entries = Object.entries(rows);
  return (
    <Card>
      <SectionLabel>{title}</SectionLabel>
      {loading ? (
        <Skeleton className="h-20 w-full" />
      ) : entries.length === 0 ? (
        <p className="text-sm text-nurev-muted">No active sessions.</p>
      ) : (
        <div className="space-y-3">
          {entries.map(([key, count]) => {
            const pct = total > 0 ? (count / total) * 100 : 0;
            return (
              <div key={key}>
                <div className="mb-1 flex justify-between text-xs">
                  <span className="capitalize text-nurev-text">{key}</span>
                  <span className="text-nurev-muted">
                    {count} ({Math.round(pct)}%)
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-white/[0.04]">
                  <div
                    className={`h-2 rounded-full ${accent} transition-all`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─── Components tab ───────────────────────────────────────────────────────

function ComponentsTab() {
  const { data: components = [], isLoading } = useInfraComponents();

  return (
    <Card>
      <SectionLabel>Component Manifests ({components.length})</SectionLabel>
      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : components.length === 0 ? (
        <p className="text-sm text-nurev-muted">No components configured.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-nurev-muted">
                <th className="pb-2 font-medium">Name</th>
                <th className="pb-2 font-medium">Type</th>
                <th className="pb-2 font-medium">Platform</th>
                <th className="pb-2 font-medium">Version</th>
                <th className="pb-2 font-medium">Algorithm</th>
                <th className="pb-2 font-medium">Enabled</th>
                <th className="pb-2 text-right font-medium">Sessions</th>
                <th className="pb-2 text-right font-medium">Speed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-nurev-border">
              {components.map(c => (
                <ComponentRowEl key={c._id} c={c} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function ComponentRowEl({ c }: { c: ComponentRow }) {
  return (
    <tr>
      <td className="py-2">
        <p className="font-medium text-nurev-text">{c.name}</p>
        {c.binaryName && (
          <p className="font-mono text-[10px] text-nurev-muted">{c.binaryName}</p>
        )}
      </td>
      <td className="py-2">
        <TypeBadge type={c.type} />
      </td>
      <td className="py-2 text-xs text-nurev-muted">
        {c.platform}
        {c.arch ? `/${c.arch}` : ''}
      </td>
      <td className="py-2 font-mono text-xs text-nurev-text">{c.version}</td>
      <td className="py-2 text-xs text-nurev-muted">{c.algorithm || '—'}</td>
      <td className="py-2">
        <span
          className={`inline-block h-2 w-2 rounded-full ${
            c.enabled ? 'bg-emerald-400' : 'bg-red-400'
          }`}
          title={c.enabled ? 'enabled' : 'disabled'}
        />
      </td>
      <td className="py-2 text-right font-mono text-nurev-text">
        {c.activeSessions || 0}
      </td>
      <td className="py-2 text-right font-mono text-nurev-text">
        {fmtNum(c.liveHashrate)}
      </td>
    </tr>
  );
}

// ─── Sessions timeline tab ────────────────────────────────────────────────

function TimelineTab() {
  const { data: timeline = [], isLoading } = useSessionsTimeline();

  const maxVal = useMemo(
    () =>
      timeline.reduce(
        (m, t) => Math.max(m, t.started, t.ended),
        1
      ),
    [timeline]
  );

  return (
    <Card>
      <SectionLabel>Sessions Started / Ended (Last 24h)</SectionLabel>
      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : timeline.length === 0 ? (
        <p className="text-sm text-nurev-muted">No session data available.</p>
      ) : (
        <>
          <div className="mb-4 flex h-32 items-end gap-px">
            {timeline.map(t => {
              const startPct = (t.started / maxVal) * 100;
              const endPct = (t.ended / maxVal) * 100;
              return (
                <div
                  key={t.hour}
                  className="flex h-full flex-1 items-end gap-px"
                  title={`${t.hour}\nStarted: ${t.started}\nEnded: ${t.ended}`}
                >
                  <div
                    className="flex-1 rounded-t bg-emerald-500/60"
                    style={{ height: `${Math.max(2, startPct)}%` }}
                  />
                  <div
                    className="flex-1 rounded-t bg-red-400/40"
                    style={{ height: `${Math.max(2, endPct)}%` }}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-[9px] text-nurev-muted">
            <span>{timeline[0]?.hour?.slice(11, 16)}</span>
            <span>
              {timeline[Math.floor(timeline.length / 2)]?.hour?.slice(11, 16)}
            </span>
            <span>{timeline[timeline.length - 1]?.hour?.slice(11, 16)}</span>
          </div>
          <div className="mt-3 flex gap-4 text-xs text-nurev-muted">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-3 rounded bg-emerald-500/60" /> Started
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-3 rounded bg-red-400/40" /> Ended
            </span>
          </div>

          <div className="mt-4 max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-nurev-surface">
                <tr className="text-left text-xs uppercase tracking-wider text-nurev-muted">
                  <th className="pb-2 font-medium">Hour</th>
                  <th className="pb-2 text-right font-medium">Started</th>
                  <th className="pb-2 text-right font-medium">Ended</th>
                  <th className="pb-2 text-right font-medium">Net</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-nurev-border">
                {timeline.map(t => {
                  const net = t.started - t.ended;
                  return (
                    <TimelineRowEl key={t.hour} row={t} net={net} />
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

function TimelineRowEl({ row, net }: { row: TimelineRow; net: number }) {
  return (
    <tr>
      <td className="py-1.5 text-xs text-nurev-muted">
        {row.hour?.slice(11, 16)}
      </td>
      <td className="py-1.5 text-right font-mono text-xs text-emerald-300">
        {row.started}
      </td>
      <td className="py-1.5 text-right font-mono text-xs text-red-300">
        {row.ended}
      </td>
      <td
        className={`py-1.5 text-right font-mono text-xs ${
          net >= 0 ? 'text-emerald-300' : 'text-red-300'
        }`}
      >
        {net >= 0 ? '+' : ''}
        {net}
      </td>
    </tr>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

const TABS = [
  { id: 'health', label: 'Health' },
  { id: 'components', label: 'Components' },
  { id: 'timeline', label: 'Sessions' }
] as const;
type Tab = (typeof TABS)[number]['id'];

export function InfrastructureScreen() {
  const [tab, setTab] = useState<Tab>('health');

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
            Infrastructure
          </h1>
          <p className="mt-1 text-xs text-nurev-muted">
            Real-time platform health. Active sessions, compute throughput,
            component live counts, 24h session flow. Health tab auto-refreshes
            every 10 seconds.
          </p>
        </div>
        <a
          href="https://app.nurev.io/infrastructure"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-nurev-muted hover:text-amber-300"
          title="Open the legacy view on the publisher dashboard"
        >
          legacy view
          <ExternalLink className="h-3 w-3" />
        </a>
      </header>

      <div className="flex w-fit gap-1 rounded-lg bg-white/[0.03] p-1">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-md px-4 py-2 text-xs font-medium transition ${
              tab === t.id
                ? 'bg-amber-500 text-nurev-bg'
                : 'text-nurev-muted hover:bg-white/[0.04] hover:text-nurev-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'health' && <HealthTab />}
      {tab === 'components' && <ComponentsTab />}
      {tab === 'timeline' && <TimelineTab />}
    </div>
  );
}
